import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2, Gift, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "rule_code", judul: "Code" },
  { kunci: "rule_name", judul: "Nama Promo" },
  { kunci: "price_group_code", judul: "Price Group" },
  { kunci: "condition_jsonb", judul: "Syarat", ambil: (p) => describeCondition(p.condition_jsonb) },
  { kunci: "bonus_jsonb", judul: "Bonus", ambil: (p) => describeBonus(p.bonus_jsonb) },
  { kunci: "effective_from", judul: "Berlaku Dari" },
  { kunci: "effective_to", judul: "Berlaku Sampai" },
  { kunci: "active", judul: "Aktif", ambil: (p) => (p.active ? "Ya" : "Tidak") },
];

const CONDITION_TYPES = [
  { value: "min_qty", label: "Minimum Qty (pcs)" },
  { value: "min_value_idr", label: "Minimum Nilai (IDR)" },
];
const BONUS_TYPES = [
  { value: "FREE_JERSEY", label: "Jersey Gratis (+N pcs)" },
  { value: "DISCOUNT_PERCENT", label: "Diskon Persen (%)" },
  { value: "DISCOUNT_AMOUNT_IDR", label: "Diskon Nominal (IDR)" },
  { value: "BUNDLING_FREE_ITEM", label: "Bundling Gratis (+item)" },
];

const describeCondition = (c) => {
  if (!c || !c.type) return "—";
  if (c.type === "min_qty") return `≥ ${c.threshold || 0} pcs`;
  if (c.type === "min_value_idr") return `≥ ${formatIDR(c.threshold)}`;
  return "—";
};
const describeBonus = (b) => {
  if (!b || !b.type) return "—";
  const tlabel = BONUS_TYPES.find((x) => x.value === b.type)?.label || b.type;
  const suffix = b.label ? ` · ${b.label}` : "";
  if (b.type === "DISCOUNT_PERCENT") return `${tlabel} → ${b.value || 0}%${suffix}`;
  if (b.type === "DISCOUNT_AMOUNT_IDR") return `${tlabel} → ${formatIDR(b.value)}${suffix}`;
  return `${tlabel} → ${b.value || 0}${suffix}`;
};

export default function MasterBonusPromo() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filterPg, setFilterPg] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPg !== "ALL") params.price_group_id = filterPg;
      const [pr, pg] = await Promise.all([
        api.get("/master/bonus-promo", { params }),
        api.get("/master/price-groups"),
      ]);
      setItems(pr.data.items || []);
      setGroups(pg.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Data promo gagal dimuat"));
    } finally { setLoading(false); }
  }, [filterPg]);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["rule_code", "rule_name", "description", "price_group_code"],
    urutAwal: { kunci: "rule_code", arah: "asc" },
  });

  const pgName = (id) => groups.find((g) => g.id === id)?.code || "—";

  const handleDelete = async () => {
    try { await api.delete(`/master/bonus-promo/${confirmDel.id}`); toast.success("Promo dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Promo gagal dihapus")); }
    finally { setConfirmDel(null); }
  };

  const noGroupsYet = groups.length === 0;

  return (
    <div className="space-y-6" data-testid="master-promo-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Gift size={22} /> Master · Bonus Promo
            <Hint>Aturan promo dan bonus berlaku per Price Group (per wilayah). Surabaya dan Samarinda+Balikpapan bisa berbeda tanpa tertukar.</Hint>
          </h1>
        </div>
        {isOwner && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} disabled={noGroupsYet} data-testid="promo-new-btn">
            <Plus size={16} className="mr-1" /> Tambah Promo
          </Button>
        )}
      </div>

      {noGroupsYet && (
        <Card className="p-4 border-warning-soft-border bg-warning-soft">
          <div className="flex items-start gap-2 text-sm text-warning-soft-foreground">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>Belum ada Price Group. Buat Price Group dulu di <a className="underline" href="/master/price-groups">Master · Price Group</a> sebelum menambah bonus promo.</div>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterPg} onValueChange={setFilterPg}>
            <SelectTrigger className="w-72" data-testid="promo-filter-pg"><SelectValue placeholder="Pilih Price Group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Price Group</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama promo…" testId="promo-palang"
                      ekspor={{ nama: "master_bonus_promo", kolom: KOLOM_CSV }} />

        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="rule_code" className="text-left px-3 py-2">Kode</ThUrut>
                  <ThUrut daftar={daftar} kunci="rule_name" className="text-left px-3 py-2">Nama Promo</ThUrut>
                  <ThUrut daftar={daftar} kunci="price_group_code" className="text-left px-3 py-2">Price Group</ThUrut>
                  <th className="text-left px-3 py-2">Syarat</th>
                  <th className="text-left px-3 py-2">Bonus</th>
                  <th className="text-left px-3 py-2">Berlaku</th>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  {isOwner && <th></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={isOwner ? 8 : 7} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : filterPg === "ALL" ? "Belum ada promo." : "Tidak ada promo untuk price group ini."}
                </td></tr>}
                {daftar.tampil.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`promo-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.rule_code}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.rule_name}</div>
                      {p.description && <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <Badge variant="outline" className="font-mono">{p.price_group_code || pgName(p.price_group_id)}</Badge>
                      {p.price_group_name && <div className="text-[10px] text-muted-foreground mt-0.5">{p.price_group_name}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">{describeCondition(p.condition_jsonb)}</td>
                    <td className="px-3 py-2 text-xs">{describeBonus(p.bonus_jsonb)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{p.effective_from || "—"} → {p.effective_to || "∞"}</td>
                    <td className="px-3 py-2">{p.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    {isOwner && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`promo-edit-${p.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(p)} className="text-danger-soft-foreground" data-testid={`promo-delete-${p.id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="promo-paginasi" />
          </div>
        )}
      </Card>

      {showForm && (
        <PromoFormModal
          promo={editing}
          groups={groups}
          defaultPgId={filterPg !== "ALL" ? filterPg : null}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Promo?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmDel?.rule_name}" akan dihapus permanen. FA yang sudah memakai promo ini tidak terpengaruh.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="promo-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PromoFormModal({ promo, groups, defaultPgId, onClose, onSaved }) {
  const c0 = promo?.condition_jsonb || {};
  const b0 = promo?.bonus_jsonb || {};
  const [f, setF] = useState({
    rule_code: promo?.rule_code || "",
    rule_name: promo?.rule_name || "",
    price_group_id: promo?.price_group_id || defaultPgId || "",
    description: promo?.description || "",
    condition_type: c0.type || "min_qty",
    condition_threshold: c0.threshold ?? "",
    bonus_type: b0.type || "FREE_JERSEY",
    bonus_value: b0.value ?? "",
    bonus_label: b0.label || "",
    effective_from: promo?.effective_from || "",
    effective_to: promo?.effective_to || "",
    active: promo?.active ?? true,
    notes: promo?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const originalPg = promo?.price_group_id;
  const pgChanged = promo && originalPg && f.price_group_id && originalPg !== f.price_group_id;

  const save = async () => {
    if (!f.rule_code.trim() || !f.rule_name.trim()) { toast.error("Kode dan Nama wajib"); return; }
    if (!f.price_group_id) { toast.error("Price Group wajib dipilih"); return; }
    if (f.condition_threshold === "" || isNaN(parseInt(f.condition_threshold))) { toast.error("Batas syarat wajib angka"); return; }
    if (f.bonus_value === "" || isNaN(parseFloat(f.bonus_value))) { toast.error("Nilai bonus wajib angka"); return; }
    setSaving(true);
    try {
      const body = {
        rule_code: f.rule_code.trim(),
        rule_name: f.rule_name.trim(),
        price_group_id: f.price_group_id,
        description: f.description || null,
        condition_jsonb: { type: f.condition_type, threshold: parseInt(f.condition_threshold) },
        bonus_jsonb: {
          type: f.bonus_type,
          value: f.bonus_type === "DISCOUNT_PERCENT" ? parseFloat(f.bonus_value) : parseInt(f.bonus_value),
          ...(f.bonus_label ? { label: f.bonus_label } : {}),
        },
        effective_from: f.effective_from || null,
        effective_to: f.effective_to || null,
        active: f.active,
        notes: f.notes || null,
      };
      if (promo) {
        delete body.rule_code; // immutable post-create
        await api.patch(`/master/bonus-promo/${promo.id}`, body);
      } else {
        await api.post("/master/bonus-promo", body);
      }
      toast.success("Promo disimpan"); onSaved();
    } catch (e) {
      const msg = apiErrorMessage(e, "Promo gagal disimpan");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="promo-form-modal">
        <DialogHeader><DialogTitle>{promo ? "Edit Promo" : "Tambah Promo"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label>Kode *</Label>
            <Input value={f.rule_code} onChange={(e) => upd("rule_code", e.target.value)} placeholder="cth: PROMO_KEMERDEKAAN_2026_SBY" disabled={!!promo} data-testid="promo-form-code" />
          </div>
          <div>
            <Label>Nama Promo *</Label>
            <Input value={f.rule_name} onChange={(e) => upd("rule_name", e.target.value)} placeholder="cth: Promo Kemerdekaan 2026 Surabaya" data-testid="promo-form-name" />
          </div>
          <div className="col-span-2">
            <Label>Price Group * <span className="text-muted-foreground">(berlaku per wilayah)</span></Label>
            <Select value={f.price_group_id} onValueChange={(v) => upd("price_group_id", v)}>
              <SelectTrigger data-testid="promo-form-pg"><SelectValue placeholder="Pilih price group (Surabaya / Samarinda+Balikpapan / dll)" /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}</SelectContent>
            </Select>
            {pgChanged && (
              <div className="mt-2 px-3 py-2 rounded-md bg-warning-soft border border-warning-soft-border flex items-start gap-2 text-xs text-warning-soft-foreground">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>Promo akan <strong>dipindah</strong> dari price group lama ke yang baru. FA yang sudah memakai promo ini tidak terpengaruh.</div>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Deskripsi</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => upd("description", e.target.value)} placeholder="Penjelasan singkat promo" />
          </div>

          <div className="col-span-2 mt-2 pt-2 border-t border-border">
            <div className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Syarat (Kapan Promo Berlaku)</div>
          </div>
          <div>
            <Label>Jenis Syarat *</Label>
            <Select value={f.condition_type} onValueChange={(v) => upd("condition_type", v)}>
              <SelectTrigger data-testid="promo-form-condition-type"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Batas *</Label>
            <Input type="number" min={0} value={f.condition_threshold} onChange={(e) => upd("condition_threshold", e.target.value)} placeholder={f.condition_type === "min_qty" ? "cth: 12" : "cth: 5000000"} data-testid="promo-form-threshold" />
          </div>

          <div className="col-span-2 mt-2 pt-2 border-t border-border">
            <div className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Bonus (Hadiah yang Diberikan)</div>
          </div>
          <div>
            <Label>Jenis Bonus *</Label>
            <Select value={f.bonus_type} onValueChange={(v) => upd("bonus_type", v)}>
              <SelectTrigger data-testid="promo-form-bonus-type"><SelectValue /></SelectTrigger>
              <SelectContent>{BONUS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nilai Bonus *</Label>
            <Input type="number" min={0} step={f.bonus_type === "DISCOUNT_PERCENT" ? "0.01" : "1"}
                   value={f.bonus_value} onChange={(e) => upd("bonus_value", e.target.value)}
                   placeholder={f.bonus_type === "DISCOUNT_PERCENT" ? "cth: 10" : f.bonus_type.includes("AMOUNT") ? "cth: 250000" : "cth: 1"} data-testid="promo-form-bonus-value" />
          </div>
          <div className="col-span-2">
            <Label>Label Bonus (opsional)</Label>
            <Input value={f.bonus_label} onChange={(e) => upd("bonus_label", e.target.value)} placeholder="cth: Gratis 1 jersey untuk tim 12+ pcs" />
          </div>

          <div className="col-span-2 mt-2 pt-2 border-t border-border">
            <div className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Masa Berlaku</div>
          </div>
          <div><Label>Berlaku Dari</Label><Input type="date" value={f.effective_from} onChange={(e) => upd("effective_from", e.target.value)} /></div>
          <div><Label>Berlaku Sampai</Label><Input type="date" value={f.effective_to} onChange={(e) => upd("effective_to", e.target.value)} /></div>
          <div className="col-span-2 flex items-center gap-2"><Checkbox checked={f.active} onCheckedChange={(v) => upd("active", !!v)} id="promoact" /><Label htmlFor="promoact" className="cursor-pointer">Aktif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="promo-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
